import React, { useEffect, useState } from 'react';

const formatLabel = (val) => {
  if (!val) return '—';
  return val.replace(/_/g, ' ');
};

const TreeDetailsPanel = ({ tree, enums = {}, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (tree) {
      setForm({
        genus: tree.genus || '',
        species: tree.species || '',
        common_name: tree.common_name || '',
        status: tree.status || 'healthy',
        form: tree.form || 'standard',
        height_m: tree.height_m ?? '',
        canopy_spread_m: tree.canopy_spread_m ?? '',
        dbh_cm: tree.dbh_cm ?? '',
        number_of_stems: tree.number_of_stems || 'single',
        zone: tree.zone || '',
        occupation: tree.occupation || '',
        gps_lat: tree.gps_lat,
        gps_lng: tree.gps_lng,
      });
      setEditing(false);
    }
  }, [tree]);

  if (!tree) {
    return (
      <div className="empty-state">
        <p>Select a tree to view details</p>
      </div>
    );
  }

  const statuses = enums.tree_status || ['healthy', 'monitor', 'priority', 'dead', 'removed', 'stump'];
  const forms = enums.tree_form || ['standard', 'multi_stem', 'pollard', 'coppice', 'weeping', 'columnar', 'spreading', 'other'];
  const stems = enums.stem_count || ['single', 'double', 'triple', 'multi_3_5', 'multi_6_plus'];
  const riskLevels = enums.risk_level || ['high', 'mid', 'low'];

  const handleSave = (e) => {
    e.preventDefault();
    onSave?.({
      ...tree,
      ...form,
      height_m: form.height_m === '' ? null : parseFloat(form.height_m),
      canopy_spread_m: form.canopy_spread_m === '' ? null : parseFloat(form.canopy_spread_m),
      dbh_cm: form.dbh_cm === '' ? null : parseFloat(form.dbh_cm),
      zone: form.zone || null,
      occupation: form.occupation || null,
    }, () => setEditing(false));
  };

  if (editing) {
    return (
      <form onSubmit={handleSave}>
        <div className="panel-section">
          <h2>Edit tree</h2>
        </div>
        <div className="form-group">
          <label>Common name</label>
          <input value={form.common_name} onChange={(e) => setForm({ ...form, common_name: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Genus</label>
          <input value={form.genus} onChange={(e) => setForm({ ...form, genus: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Species</label>
          <input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {statuses.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Form</label>
          <select value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })}>
            {forms.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Stems</label>
          <select value={form.number_of_stems} onChange={(e) => setForm({ ...form, number_of_stems: e.target.value })}>
            {stems.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Height (m)</label>
          <input type="number" step="0.1" value={form.height_m} onChange={(e) => setForm({ ...form, height_m: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Canopy spread (m)</label>
          <input type="number" step="0.1" value={form.canopy_spread_m} onChange={(e) => setForm({ ...form, canopy_spread_m: e.target.value })} />
        </div>
        <div className="form-group">
          <label>DBH (cm)</label>
          <input type="number" step="0.1" value={form.dbh_cm} onChange={(e) => setForm({ ...form, dbh_cm: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Zone</label>
          <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>
            <option value="">—</option>
            {riskLevels.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Occupation</label>
          <select value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}>
            <option value="">—</option>
            {riskLevels.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
        </div>
        <div className="inline-actions">
          <button type="button" className="btn btn-secondary btn-sm" style={{ color: '#333', background: '#eee' }} onClick={() => setEditing(false)}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm">Save</button>
        </div>
      </form>
    );
  }

  const statusClass = `tag tag-${tree.status || 'healthy'}`;

  return (
    <div>
      <div className="panel-section panel-section-header">
        <h2>{tree.common_name || `${tree.genus} ${tree.species}`}</h2>
        <div className="panel-meta-row">
          <span className={statusClass}>{formatLabel(tree.status)}</span>
          <button type="button" className="btn btn-secondary btn-sm btn-edit" onClick={() => setEditing(true)}>Edit</button>
        </div>
      </div>

      <div className="panel-section">
        <h3>Identification</h3>
        <div className="field-row"><span className="field-label">Genus</span><span className="field-value">{tree.genus || '—'}</span></div>
        <div className="field-row"><span className="field-label">Species</span><span className="field-value">{tree.species || '—'}</span></div>
        <div className="field-row"><span className="field-label">Form</span><span className="field-value">{formatLabel(tree.form)}</span></div>
        <div className="field-row"><span className="field-label">Stems</span><span className="field-value">{formatLabel(tree.number_of_stems)}</span></div>
      </div>

      <div className="panel-section">
        <h3>Measurements</h3>
        <div className="field-row"><span className="field-label">Height (m)</span><span className="field-value">{tree.height_m ?? '—'}</span></div>
        <div className="field-row"><span className="field-label">Canopy (m)</span><span className="field-value">{tree.canopy_spread_m ?? '—'}</span></div>
        <div className="field-row"><span className="field-label">DBH (cm)</span><span className="field-value">{tree.dbh_cm ?? '—'}</span></div>
      </div>

      <div className="panel-section">
        <h3>Location</h3>
        <div className="field-row"><span className="field-label">GPS</span><span className="field-value">{tree.gps_lat?.toFixed(5)}, {tree.gps_lng?.toFixed(5)}</span></div>
      </div>
    </div>
  );
};

export default TreeDetailsPanel;
